import {
    Component,
    OnInit,
    OnDestroy,
    signal,
    WritableSignal,
    effect,
    inject,
    HostListener,
    ElementRef,
    ViewChild,
} from '@angular/core'
import { forkJoin, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import {
    HOME_DROPDOWN_SOURCE,
    HomeDashboardDropdownCoordinatorService,
} from './services/home-dashboard-dropdown.coordinator'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import {
    IActivity,
    ICalendarDay,
    IHighlight,
    IImportantDate,
    IStatCard,
    ISummaryItem,
    ITask,
} from '../../core/models/dashboard.model'
import { WEEKDAYS, AppConstants } from '../../core/constants/app.constants'
import { valueConstants } from '@app/core/constants/valueConstants'
import { IDropdownOption } from '@app/core/models/configurable-components.model'
import { INewLeadsCreatedApiOptions } from '@app/core/models/chart.model'
import { EProductType } from '@app/core/enum/pricing.enum'
import {
    generateCalendarDays,
    getMonthYearString,
    getNextMonth,
    getPreviousMonth,
} from '../../core/utilities/date.utils'
import { UserStore } from '@core/services/user/user.store'
import { authEndpoints } from '@app/core/constants/valueConstants'
import { environment } from 'src/environments/environment'
import { SalesFunnelService } from '@core/services/sales-funnel/sales-funnel.service'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { PERMISSIONS } from '@app/core/constants/permissions.constants'
import { getUserId } from '@app/core/helpers/user.helper'
import {
    LEAD_DASHBOARD_QUERY_PARAMS,
    HOME_DASHBOARD_QUERY_PARAMS,
} from '@app/core/constants/app.constants'
import { Router, ActivatedRoute } from '@angular/router'
import { navigateWithMergedQueryParams } from '@app/core/utilities/router.utils'
import { ReporteesService } from '@core/services/reportees/reportees.service'
import { IReporteesData } from '@app/core/models/reportees.model'
import { DashboardHeaderComponent } from './components/dashboard-header/dashboard-header.component'
import { CoreMetricsComponent } from './components/core-metrics/core-metrics.component'
import { MonthlyLeaderboardComponent } from './components/monthly-leaderboard/monthly-leaderboard.component'
import { AlertsStripComponent } from './components/alerts-strip/alerts-strip.component'
import { SalesFunnelOverviewComponent } from './components/sales-funnel-overview/sales-funnel-overview.component'
import { DealsPipelineComponent } from './components/deals-pipeline/deals-pipeline.component'
import { LeadSourcesComponent } from './components/lead-sources/lead-sources.component'
import { NewLeadsCreatedComponent } from './components/new-leads-created/new-leads-created.component'
import { LeadConversionMetricsComponent } from './components/lead-conversion-metrics/lead-conversion-metrics.component'
import { RMPerformanceDashboardComponent } from './components/rm-performance-dashboard/rm-performance-dashboard.component'
import { HOME_DASHBOARD_COMING_SOON_TEXT } from './constants/home-dashboard.constants'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [
        DashboardHeaderComponent,
        CoreMetricsComponent,
        MonthlyLeaderboardComponent,
        AlertsStripComponent,
        SalesFunnelOverviewComponent,
        DealsPipelineComponent,
        LeadSourcesComponent,
        NewLeadsCreatedComponent,
        LeadConversionMetricsComponent,
        RMPerformanceDashboardComponent,
        SvgIconComponent,
    ],
})
export class HomeComponent implements OnInit, OnDestroy {
    @ViewChild('newLeadsFiltersHost', { read: ElementRef })
    private newLeadsFiltersHostRef?: ElementRef<HTMLElement>

    private dashboardService = inject(IDashboardService)
    private userStore = inject(UserStore)
    private salesFunnelService = inject(SalesFunnelService)
    permissionService = inject(PermissionService)
    private reporteesService = inject(ReporteesService)
    private router = inject(Router)
    private route = inject(ActivatedRoute)
    private readonly dropdownCoordinator = inject(HomeDashboardDropdownCoordinatorService)

    stats: IStatCard[] = []
    activities: IActivity[] = []
    tasks: ITask[] = []
    summaryItems: ISummaryItem[] = []
    highlights: IHighlight[] = []
    importantDates: IImportantDate[] = []
    calendarDays: ICalendarDay[] = []
    currentDate = new Date()
    weekdays = WEEKDAYS
    isLoading: WritableSignal<boolean> = signal(true)
    isAuthenticated: WritableSignal<boolean> = signal(false)
    // Skeleton loading arrays
    readonly statsSkeleton = Array(6).fill(0)
    readonly summarySkeleton = Array(3).fill(0)
    readonly highlightsSkeleton = Array(2).fill(0)
    readonly activitySkeleton = Array(3).fill(0)
    readonly taskSkeleton = Array(3).fill(0)
    readonly calendarSkeleton = Array(42).fill(0)
    readonly datesSkeleton = Array(2).fill(0)
    readonly PERMISSIONS = PERMISSIONS
    readonly AppConstants = AppConstants
    readonly valueConstants = valueConstants
    readonly homeDashboardComingSoonText = HOME_DASHBOARD_COMING_SOON_TEXT
    hasReportees = signal<boolean>(false)
    reporteesData = signal<IReporteesData | null>(null)
    zmUserId: string | undefined
    selectedTabIndex = 0
    zoneInfo = signal<{
        zoneName: string
        teamMembersCount: number
        citiesCount: number
    } | null>(null)
    zoneInfoLoading = signal<boolean>(false)
    private readonly newLeadsOptions = signal<INewLeadsCreatedApiOptions | null>(null)
    selectedCity: string = valueConstants.ALL_CITIES
    selectedRM: string = AppConstants.ALL_REPS
    selectedProduct: string = AppConstants.ALL_PRODUCTS
    rmNames: string[] = []
    private _apiSalespersonOptions: string[] = []

    // Dropdown states for new-leads filters
    isCityDropdownOpen = signal<boolean>(false)
    isRMDropdownOpen = signal<boolean>(false)
    isProductDropdownOpen = signal<boolean>(false)

    cityFilterOptions: IDropdownOption[] = [
        { label: valueConstants.ALL_CITIES, value: valueConstants.ALL_CITIES },
        { label: 'Vadodara', value: 'Vadodara' },
    ]

    rmFilterOptions: IDropdownOption[] = [
        { label: AppConstants.ALL_REPS, value: AppConstants.ALL_REPS },
    ]

    productFilterOptions: IDropdownOption[] = [
        { label: AppConstants.ALL_PRODUCTS, value: AppConstants.ALL_PRODUCTS },
        { label: EProductType.MINDSPARK, value: EProductType.MINDSPARK },
        { label: EProductType.CARES, value: EProductType.CARES },
        { label: EProductType.ASSET, value: EProductType.ASSET },
        { label: EProductType.CREST, value: EProductType.CREST },
        { label: EProductType.MIRA, value: EProductType.MIRA },
    ]

    private destroy$ = new Subject<void>()
    private lastAcademicYearStart: number | null = null

    constructor() {
        effect(() => {
            const userData = this.userStore.userData()
            this.isAuthenticated.set(!!userData)
        })

        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            const userData = this.userStore.getUserData()
            if (!userData?.user_id || !this.hasHomeDashboardAccess()) {
                return
            }
            const isTeamView = this.hasReportees() && this.selectedTabIndex === 1
            this.salesFunnelService.loadSalesFunnelData(undefined, isTeamView)
        })

        effect(() => {
            const options = this.newLeadsOptions()
            if (options) {
                this.tryBuildZoneInfo()
            }
        })
    }

    async ngOnInit(): Promise<void> {
        this.dropdownCoordinator.opened$
            .pipe(
                filter((id) => id !== HOME_DROPDOWN_SOURCE.NEW_LEADS_FILTERS),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.closeAllNewLeadsDropdowns())

        const userData = this.userStore.getUserData()
        if (!userData?.user_id) {
            this.isLoading.set(false)
            return
        }

        await this.permissionService.getPermissionsBitwise()
        if (!this.hasHomeDashboardAccess()) {
            this.isLoading.set(false)
            return
        }

        this.zmUserId = userData.user_id
        await this.checkReportees()
        this.applyHomeTabFromRoute()

        if (this.hasReportees()) {
            this.setupZmQueryParams()
        }

        this.loadDashboardData()

        const isTeamView = this.hasReportees() && this.selectedTabIndex === 1
        // Bypass sales-funnel cache so funnel matches fresh dashboard data after returning from CRM
        this.salesFunnelService.loadSalesFunnelData(undefined, isTeamView, true)
    }

    hasHomeDashboardAccess(): boolean {
        return !!(
            this.permissionService.getPermissionByName(PERMISSIONS.MY_MATRIX) ||
            this.permissionService.getPermissionByName(PERMISSIONS.MY_MATRIX_VIEW) ||
            this.permissionService.getPermissionByName(PERMISSIONS.MY_DEALS) ||
            this.permissionService.getPermissionByName(PERMISSIONS.MY_FUNNEL)
        )
    }

    /** True when logged-in user should see Team View tab by default.
     *  Current rule: if user does NOT have prospect_lead permission, default to Team View.
     */
    private shouldDefaultToTeamView(): boolean {
        return !this.permissionService.getPermissionByName(PERMISSIONS.PROSPECT_LEAD)
    }

    private async checkReportees(): Promise<void> {
        const userId = this.userStore.getUserData()?.user_id
        if (!userId) {
            return
        }

        return new Promise((resolve) => {
            this.reporteesService
                .getReportees(userId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        const hasReportees = response.success && !!response.data?.reportees?.length
                        this.hasReportees.set(hasReportees)
                        if (hasReportees && response.data) {
                            this.reporteesData.set(response.data)
                            this.tryBuildZoneInfo()
                            // Team View tab default
                            if (this.shouldDefaultToTeamView()) {
                                this.selectedTabIndex = 1
                            }
                        }
                        resolve()
                    },
                    error: () => {
                        this.hasReportees.set(false)
                        this.reporteesData.set(null)
                        this.zoneInfo.set(null)
                        this.zoneInfoLoading.set(false)
                        resolve()
                    },
                })
        })
    }

    private tryBuildZoneInfo(): void {
        const hasReportees = this.hasReportees()
        const reporteesData = this.reporteesData()
        const options = this.newLeadsOptions()

        if (!hasReportees || !reporteesData?.manager) {
            this.zoneInfo.set(null)
            this.zoneInfoLoading.set(false)
            return
        }

        if (options) {
            this.zoneInfo.set({
                zoneName: reporteesData.manager.territory_name,
                teamMembersCount: reporteesData.reportees.length,
                citiesCount: options.cities.length,
            })
            this.zoneInfoLoading.set(false)
            return
        }

        // Show skeleton only when user is on Team tab and we don't have leads-created options yet.
        this.zoneInfoLoading.set(this.selectedTabIndex === 1)
    }

    private setupZmQueryParams(): void {
        if (this.zmUserId && !this.selectedTabIndex) {
            // Set query params for "My Data" tab (lead dashboard with ZM user ID)
            navigateWithMergedQueryParams(this.router, this.route, {
                [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: this.zmUserId,
            })
        }
    }

    onTabChange(index: number): void {
        this.selectedTabIndex = index
        if (!index && this.zmUserId) {
            // "My Data" tab - set query params for lead dashboard
            navigateWithMergedQueryParams(this.router, this.route, {
                [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: this.zmUserId,
                [HOME_DASHBOARD_QUERY_PARAMS.TAB]: valueConstants.USER_VIEW_QUERY_PARAM,
            })
        } else if (index === 1) {
            // "Team View" tab - remove query params
            navigateWithMergedQueryParams(this.router, this.route, {
                [LEAD_DASHBOARD_QUERY_PARAMS.USER_ID]: null,
                [HOME_DASHBOARD_QUERY_PARAMS.TAB]: valueConstants.TEAM_VIEW_QUERY_PARAM,
            })
        }

        const userData = this.userStore.getUserData()
        if (userData?.user_id) {
            const isTeamView = this.hasReportees() && index === 1
            this.salesFunnelService.loadSalesFunnelData(undefined, isTeamView)
        }

        this.tryBuildZoneInfo()
    }

    private applyHomeTabFromRoute(): void {
        if (!this.hasReportees()) {
            return
        }
        const tabParam = this.route.snapshot.queryParamMap.get(HOME_DASHBOARD_QUERY_PARAMS.TAB)
        if (tabParam === valueConstants.TEAM_VIEW_QUERY_PARAM) {
            this.selectedTabIndex = 1
            return
        }
        if (tabParam === valueConstants.USER_VIEW_QUERY_PARAM) {
            this.selectedTabIndex = 0
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private loadDashboardData(): void {
        this.isLoading.set(true)
        forkJoin({
            stats: this.dashboardService.getStats(),
            activities: this.dashboardService.getActivities(),
            tasks: this.dashboardService.getTasks(),
            summaryItems: this.dashboardService.getSummaryItems(),
            highlights: this.dashboardService.getHighlights(),
            importantDates: this.dashboardService.getImportantDates(),
            calendarEvents: this.dashboardService.getCalendarEvents(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.stats = data.stats
                    this.activities = data.activities
                    this.tasks = data.tasks
                    this.summaryItems = data.summaryItems
                    this.highlights = data.highlights
                    this.importantDates = data.importantDates
                    this.calendarDays = generateCalendarDays(this.currentDate, data.calendarEvents)
                    this.isLoading.set(false)
                },
                error: () => {
                    this.isLoading.set(false)
                },
            })
    }

    getMonthYearString(): string {
        return getMonthYearString(this.currentDate)
    }

    previousMonth(): void {
        this.currentDate = getPreviousMonth(this.currentDate)
        this.loadDashboardData()
    }

    nextMonth(): void {
        this.currentDate = getNextMonth(this.currentDate)
        this.loadDashboardData()
    }

    toggleTask(task: ITask): void {
        task.completed = !task.completed
    }

    onTaskStatusChange(task: ITask, completed: boolean): void {
        task.completed = completed
    }

    onRefresh(): void {
        this.loadDashboardData()
    }

    signIn(): void {
        const nextUrl = window.location.href
        window.location.href = `${environment.ssoBaseUrl}${authEndpoints.SIGNIN_PATH}?next=${nextUrl}`
    }

    onCityFilterChange(city: string): void {
        this.selectedCity = city
    }

    onRMFilterChange(rm: string): void {
        this.selectedRM = rm
    }

    onProductFilterChange(product: string): void {
        this.selectedProduct = product
    }

    onNewLeadsFiltersLoaded(options: INewLeadsCreatedApiOptions): void {
        this.newLeadsOptions.set(options)
        this.cityFilterOptions = [
            { label: valueConstants.ALL_CITIES, value: valueConstants.ALL_CITIES },
            ...options.cities.map((city) => ({ label: city, value: city })),
        ]
        this._apiSalespersonOptions = this._getNormalizedFilterValues(options.salespersons)
        // Merge API list with any RM names already emitted (e.g. from performance widget) so we do not drop them.
        this._updateRMFilterOptions([...this._apiSalespersonOptions, ...this.rmNames])

        this.productFilterOptions = [
            { label: AppConstants.ALL_PRODUCTS, value: AppConstants.ALL_PRODUCTS },
            ...options.products.map((product) => ({ label: product, value: product })),
        ]
        this.tryBuildZoneInfo()
    }

    onRMNamesChange(rmNames: string[]): void {
        this.rmNames = this._getNormalizedFilterValues(rmNames)
        // Keep API salespersons as source of truth and include any additional runtime names.
        this._updateRMFilterOptions([...this._apiSalespersonOptions, ...this.rmNames])
    }

    // Dropdown toggle method for new-leads filters
    toggleNewLeadsDropdown(type: 'city' | 'rm' | 'product'): void {
        this.dropdownCoordinator.notifyOpened(HOME_DROPDOWN_SOURCE.NEW_LEADS_FILTERS)
        this.isCityDropdownOpen.set(type === 'city' ? !this.isCityDropdownOpen() : false)
        this.isRMDropdownOpen.set(type === 'rm' ? !this.isRMDropdownOpen() : false)
        this.isProductDropdownOpen.set(type === 'product' ? !this.isProductDropdownOpen() : false)
    }

    // Filter selection methods for new-leads filters
    selectCityFilter(value: string | number): void {
        const stringValue = String(value)
        this.selectedCity = stringValue
        this.isCityDropdownOpen.set(false)
        this.onCityFilterChange(stringValue)
    }

    selectRMFilter(value: string | number): void {
        const stringValue = String(value)
        this.selectedRM = stringValue
        this.isRMDropdownOpen.set(false)
        this.onRMFilterChange(stringValue)
    }

    selectProductFilter(value: string | number): void {
        const stringValue = String(value)
        this.selectedProduct = stringValue
        this.isProductDropdownOpen.set(false)
        this.onProductFilterChange(stringValue)
    }

    // Close all dropdowns
    closeAllNewLeadsDropdowns(): void {
        this.isCityDropdownOpen.set(false)
        this.isRMDropdownOpen.set(false)
        this.isProductDropdownOpen.set(false)
    }

    @HostListener('document:click', ['$event'])
    handleDocumentClick(event: MouseEvent): void {
        const host = this.newLeadsFiltersHostRef?.nativeElement
        if (!host) {
            this.closeAllNewLeadsDropdowns()
            return
        }
        const target = event.target as Node | null
        if (target && host.contains(target)) {
            return
        }
        this.closeAllNewLeadsDropdowns()
    }

    private _getNormalizedFilterValues(values: string[]): string[] {
        const normalized = values
            .map((value) => value?.trim())
            .filter((value): value is string => !!value)
        return Array.from(new Set(normalized))
    }

    private _updateRMFilterOptions(values: string[]): void {
        this.rmFilterOptions = [
            { label: AppConstants.ALL_REPS, value: AppConstants.ALL_REPS },
            ...this._getNormalizedFilterValues(values).map((name) => ({
                label: name,
                value: name,
            })),
        ]
    }
}
